import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {Message} from "types/Message";
import {useNotifications} from "context/NotificationContext";
import Button from "components/Button";
import {BeakerIcon, PaperAirplaneIcon, PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DeleteMessage from "pages/Message/Delete";
import {toTitleCase} from 'services/string';
import {formattedDateTime} from 'services/date';
import Chip from "components/Chip";
import {invalidate} from "services/query-client";
import MessageStatus from "types/MessageStatus";
import DotBadge from "components/DotBadge";
import TestMessage from "../Test";

// Map message status to badge color
const statusColors: Record<MessageStatus, number> = {
  [MessageStatus.DRAFT]: DotBadge.colors.GRAY,
  [MessageStatus.SCHEDULED]: DotBadge.colors.BLUE,
  [MessageStatus.SENDING]: DotBadge.colors.YELLOW,
  [MessageStatus.SENT]: DotBadge.colors.GREEN,
}

function ViewMessage() {
  const {id} = useParams()
  const {data: message, isLoading} = useQuery(
    ['message', {id}],
    () => ngage.messages.get(parseInt(id as string))
  )

  const [deletingMessage, setDeletingMessage] = useState<Message | null>(null)
  const [testingMessage, setTestingMessage] = useState<Message | null>(null)

  const {mutateAsync: sendMessage, isLoading: isSendingMessage} = useMutation(ngage.messages.send)

  const {notify} = useNotifications()
  const navigate = useNavigate()

  // Prepare action handlers
  const handleDeleteMessage = () => {
    if (message) setDeletingMessage(message)
  }

  const handleEditMessage = () => {
    if (id) navigate(`/messages/${id}/edit`)
  }

  const handleTestMessage = () => {
    if (message) setTestingMessage(message)
  }

  const handleSendMessage = async () => {
    if (message) {
      try {
        await sendMessage(message.id)
        notify("success", "Message Sent", `${message.subject} has been sent to ${message.audience_size} people`)

        await invalidate(`messages.${message.id}`)
        await invalidate('messages.all')
      } catch (error) {
        notify("error", "Message Not Sent", `${message.subject} could not be sent`)
      }
    }
  }

  // Prepare page actions
  const actions = <>
    <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteMessage}>Delete...</Button>
    <Button color={Button.colors.WHITE} icon={PencilIcon} onClick={handleEditMessage} disabled={!!message?.last_sent_at}>Edit</Button>
    <Button color={Button.colors.WHITE} icon={BeakerIcon} onClick={handleTestMessage}>Test...</Button>
    <Button
      color={Button.colors.PRIMARY}
      icon={PaperAirplaneIcon}
      onClick={handleSendMessage}
      showSpinner={isSendingMessage}
      disabled={!message || message.status === MessageStatus.SENDING}>
      Send Now
    </Button>
  </>

  return (
    <Page
      title={message?.subject || "View Message"}
      breadcrumbs={{
        "Content": "/",
        "Messages": "/messages",
        [message?.subject || "View Message"]: `/messages/${id}`
      }}
      actions={actions}
      isLoading={isLoading}
    >
      {message &&
        <>
          {/* Message Information */}
          <section>
            <Card heading="Message Information"
                  subheading={`A ${message.recurrence ? 'repeating' : 'one-off'} message`}>
              <DescriptionList>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Status</DescriptionTerm>
                  <DescriptionDetails>
                    <DotBadge color={statusColors[message.status]} text={toTitleCase(message.status)}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Subject</DescriptionTerm>
                  <DescriptionDetails>
                    <p className="whitespace-pre-line">{message.subject}</p>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Snippet</DescriptionTerm>
                  <DescriptionDetails>
                    <p className="whitespace-pre-line">{message.snippet}</p>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Body</DescriptionTerm>
                  <DescriptionDetails>
                    <p className="whitespace-pre-line">{message.body}</p>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Action</DescriptionTerm>
                  <DescriptionDetails>
                    {!!message.action && <p>{message.action.text}</p>}

                    {!message.action && <span>Open App (Default)</span>}
                  </DescriptionDetails>
                </DescriptionListItem>
              </DescriptionList>
            </Card>
          </section>

          {/* Offers */}
          {message.offers.length > 0 &&
            <div className="mt-4">
              <Card heading="Offers">
                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {message.offers.map((offer, index) => (
                    <li key={index}>
                      <Chip
                        name={offer.name}
                        description={offer.incentive}
                        href={`/offers/${offer.id}`}
                        backgroundSrc={offer.image?.preview_url}
                      />
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          }

          {/* Recipients and Sending */}
          <section className="mt-4">
            <Card heading="Recipients and Sending">
              <DescriptionList>
                <DescriptionListItem span={1}>
                  <DescriptionTerm>Current Audience</DescriptionTerm>
                  <DescriptionDetails>
                    {message.audience_segment ? message.audience_segment.name : 'Custom Segment'} (~{message.audience_size} {message.audience_size === 1 ? 'person' : 'people'})
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Reach (To Date)</DescriptionTerm>
                  <DescriptionDetails>
                    {`${message.reach} ${message.reach === 1 ? 'person' : 'people'}`}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Delivery Channels</DescriptionTerm>
                  <DescriptionDetails>
                    {message.channels.map(toTitleCase).join(', ')}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Resends</DescriptionTerm>
                  <DescriptionDetails>
                    <p className="first-letter:uppercase">
                      {message.recurrence ? message.recurrence.toText() : 'Never'}
                    </p>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Recipient Cooldown</DescriptionTerm>
                  <DescriptionDetails>
                    {message.cooldown.toHuman()}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Next Due At</DescriptionTerm>
                  <DescriptionDetails>
                    {message.due_at ? formattedDateTime(message.due_at) : 'Never'}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Last Sent</DescriptionTerm>
                  <DescriptionDetails>
                    {message.last_sent_at ? formattedDateTime(message.last_sent_at) : 'Never'}
                  </DescriptionDetails>
                </DescriptionListItem>

              </DescriptionList>
            </Card>
          </section>
        </>
      }

      {/* Test Confirmation modal */}
      <TestMessage
        show={!!testingMessage}
        message={message ?? null}
        onCancel={() => setTestingMessage(null)}
        onSend={() => setTestingMessage(null)}
      />

      {/* Delete confirmation modal */}
      <DeleteMessage
        show={message === deletingMessage}
        message={deletingMessage}
        onCancel={() => setDeletingMessage(null)}
        onDelete={() => navigate('/messages')}
        onDeleteFail={() => setDeletingMessage(null)}
      />
    </Page>
  );
}

export default ViewMessage;